import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import '../styles/CompanyPage.scss'
import { DateTime } from 'luxon'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useShortScale } from '../../../hooks/useShortScale'
import { AddBasicDetailsModal } from './AddBasicDetailsModal'
import { AddIndustryFocusModal } from './AddIndustryFocusModal'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

export const Overview = ({
  companyPageData,
  setTotalFunds,
  totalFunds,
  setFirstDate,
  firstDate,
  companyInvestmentData,
  setLeadInvestors,
  leadInvestors,
  companyInvestorsData,
  getCompanyPageData,
  industryFocusData,
  getAllIndustryFocus,
  publicStatus,
  companyApiLoading,
}: {
  companyPageData: any
  setTotalFunds: Dispatch<SetStateAction<number>>
  totalFunds: number
  setFirstDate: Dispatch<SetStateAction<string>>
  firstDate: string
  companyInvestmentData: any
  setLeadInvestors: any
  leadInvestors: any
  companyInvestorsData: any
  getCompanyPageData: any
  industryFocusData: any
  getAllIndustryFocus: any
  publicStatus: boolean | undefined
  companyApiLoading: boolean
}) => {
  const { formatMessage } = useIntl()
  const { getDateValue } = useDateFormat()
  const { convertValueToShortScale } = useShortScale()
  const [showBasicDetailsModal, setShowBasicDetailsModal] = useState(false)
  const [showIndustryFocusModal, setShowIndustryFocusModal] = useState(false)

  useEffect(() => {
    const totalFund = companyInvestmentData?.reduce(
      (sum: number, investment: any) => sum + Number(investment?.money_raised),
      0
    )
    setTotalFunds(totalFund)
    if (companyInvestmentData?.length > 0) {
      setFirstDate(companyInvestmentData[companyInvestmentData?.length - 1]?.announced_date)
    }
  }, [companyInvestmentData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const leadInvestors = companyInvestorsData?.filter(
      (investor: any) => investor.lead_investor === 'YES'
    )
    setLeadInvestors(leadInvestors)
  }, [companyInvestorsData]) // eslint-disable-line react-hooks/exhaustive-deps

  console.log('chekcing loading data', companyApiLoading)

  return (
    <>
      <div>
        <div className='company-page-overview d-flex flex-md-row flex-column gap-6'>
          <div className='bg-white p-6 company-page-highlights rounded-1 company-page-dark'>
            <div className='d-flex justify-content-between mt-2'>
              <h4 className='font-size-16 font-weight-600'>
                {formatMessage({ id: 'Funding Highlights' })}
              </h4>
            </div>
            <div className='mt-4'>
              <div className='p-0 m-0'>
                <p className='font-weight-500 p-0 m-0 dark-text-clr88 '>
                  {formatMessage({ id: 'Funding Rounds' })}
                </p>
                {companyApiLoading ? (
                  <Skeleton
                    height={34}
                    style={{ marginBottom: '10px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <p className='font-size-16 font-weight-600'>{companyInvestmentData?.length}</p>
                )}
              </div>
              <div className='p-0 m-0'>
                <p className='font-weight-500 p-0 m-0 dark-text-clr88 '>
                  {formatMessage({ id: 'Total Funding Amount' })}
                </p>
                {companyApiLoading ? (
                  <Skeleton
                    height={34}
                    style={{ marginBottom: '10px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <p className='font-size-16 font-weight-600'>
                    ${convertValueToShortScale(totalFunds)}
                  </p>
                )}
              </div>

              <div className='p-0 m-0'>
                <p className='font-weight-500 p-0 m-0 dark-text-clr88 '>
                  {formatMessage({ id: 'Lead Investors' })}
                </p>
                {companyApiLoading ? (
                  <Skeleton
                    height={34}
                    style={{ marginBottom: '10px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <p className='font-size-16 font-weight-600'>{leadInvestors?.length}</p>
                )}
              </div>

              <div className='p-0 m-0'>
                <p className='font-weight-500 p-0 m-0 dark-text-clr88'>
                  {formatMessage({ id: 'Investors' })}
                </p>
                {companyApiLoading ? (
                  <Skeleton
                    height={34}
                    style={{ marginBottom: '10px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <p className='font-size-16 font-weight-600'>{companyInvestorsData?.length}</p>
                )}
              </div>
            </div>
          </div>
          <div className='company-page-details bg-white p-6 rounded-1 company-page-dark'>
            <div className='d-flex flex-md-row flex-column gap-2 justify-content-between mt-2'>
              <h4 className='font-size-16 font-weight-600'>
                {formatMessage({ id: 'Basic Details' })}
              </h4>
              {publicStatus === false && (
                <button
                  className='btn bg-light custom-button-property text-muted py-3'
                  onClick={() => setShowBasicDetailsModal(true)}
                >
                  {formatMessage({ id: 'Edit' })}
                </button>
              )}
            </div>
            <div>
              <div className='content'>
                {companyApiLoading ? (
                  <Skeleton
                    width={150}
                    height={44}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <div>
                    <p className='font-size-12 font-weight-400 my-2 text-clr58'>
                      {formatMessage({ id: 'Founded Date' })}
                    </p>
                    <p className='font-size-13 font-weight-400 text-clr38 dark_text_color'>
                      {companyPageData?.foundedDate
                        ? getDateValue(new Date(companyPageData?.foundedDate).toISOString())
                          ? getDateValue(new Date(companyPageData?.foundedDate).toISOString())
                          : DateTime.fromISO(
                            new Date(companyPageData?.foundedDate).toISOString()
                          ).toLocaleString(DateTime.DATE_MED)
                        : '-'}
                    </p>
                  </div>
                )}
                {companyApiLoading ? (
                  <Skeleton
                    width={150}
                    height={44}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <div>
                    <p className='font-size-12 font-weight-400 my-2 text-clr58'>
                      {formatMessage({ id: 'Founders' })}
                    </p>
                    <p className='font-size-13 font-weight-400 text-clr38 text-wrap dark_text_color'>
                      {companyPageData?.founders ? companyPageData?.founders : '-'}
                    </p>
                  </div>
                )}
                {companyApiLoading ? (
                  <Skeleton
                    width={150}
                    height={44}
                    style={{ marginTop: '13px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <div>
                    <p className='font-size-12 font-weight-400 my-2 text-clr58'>
                      {formatMessage({ id: 'Operating Status' })}
                    </p>
                    <p className='font-size-13 font-weight-400 text-clr38 dark_text_color'>
                      {companyPageData?.operatingStatus ? companyPageData?.operatingStatus : '-'}
                    </p>
                  </div>
                )}
                {companyApiLoading ? (
                  <Skeleton
                    width={150}
                    height={44}
                    style={{ marginTop: '13px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <div>
                    <p className='font-size-12 font-weight-400 my-2 text-clr58'>
                      {formatMessage({ id: 'Last Funding Type' })}
                    </p>
                    <p className='font-size-13 font-weight-400 text-clr38 dark_text_color'>
                      {companyPageData?.lastFundingType ? companyPageData?.lastFundingType : '-'}
                    </p>
                  </div>
                )}
                {companyApiLoading ? (
                  <Skeleton
                    width={150}
                    height={44}
                    style={{ marginTop: '13px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <div>
                    <p className='font-size-12 font-weight-400 my-2 text-clr58'>
                      {formatMessage({ id: 'Legal Name' })}
                    </p>
                    <p className='font-size-13 font-weight-400 text-clr38  text-wrap dark_text_color'>
                      {companyPageData?.legalName ? companyPageData?.legalName : '-'}
                    </p>
                  </div>
                )}
                {companyApiLoading ? (
                  <Skeleton
                    width={150}
                    height={44}
                    style={{ marginTop: '13px' }}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <div>
                    <p className='font-size-12 font-weight-400 my-2 text-clr58'>
                      {formatMessage({ id: 'Operating Countries' })}
                    </p>
                    <p className='font-size-13 font-weight-400 text-clr38 dark_text_color'>
                      {companyPageData?.operatingCountries
                        ? companyPageData?.operatingCountries
                        : '-'}
                    </p>
                  </div>
                )}
              </div>
              {companyApiLoading ? (
                <Skeleton
                  height={44}
                  style={{ marginTop: '25px', width: '90%' }}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='mt-4 text-clr58 text-wrap dark_text_color'>
                  {firstDate &&
                    companyPageData &&
                    companyInvestmentData?.length > 0 &&
                    formatMessage({
                      id: `${companyPageData?.name
                        } has raised a total of $${convertValueToShortScale(
                          totalFunds
                        )} in funding over ${companyInvestmentData?.length} rounds. Their latest
              funding was raised on ${DateTime.fromISO(
                          new Date(firstDate).toISOString()?.toString()
                        ).toLocaleString(DateTime.DATE_MED)} from a ${companyInvestmentData[companyInvestmentData?.length - 1]?.transaction_name
                        }.`,
                    })}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className='bg-white p-6 industry-focus-container mt-6 rounded-1 company-page-dark'>
          <div className='d-flex flex-md-row flex-column gap-2 justify-content-between mt-2'>
            <h4 className='font-size-16 font-weight-600'>
              {formatMessage({ id: 'Industry Focus' })}
            </h4>
            {publicStatus === false && (
              <button
                className='btn bg-light text-muted custom-button-property py-3'
                onClick={() => setShowIndustryFocusModal(true)}
              >
                {formatMessage({ id: 'Edit' })}
              </button>
            )}
          </div>
          <div className='mt-0 d-flex gap-4 industry-focus flex-wrap'>
            {companyApiLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                <Skeleton
                  key={index}
                  className='  px-7 py-3 me-5 mb-md-0 mb-5'
                  width={100}
                  height={35}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ))
              : industryFocusData &&
              industryFocusData?.map(({ industry }: any, index: any) => (
                <div
                  key={index}
                  className='card-container border px-7 py-3 rounded-pill me-5 mb-md-0 mb-5'
                >
                  <div className='font-size-13'>{industry}</div>
                </div>
              ))}
          </div>
        </div>
      </div>
      <AddBasicDetailsModal
        setShowBasicDetailsModal={setShowBasicDetailsModal}
        showBasicDetailsModal={showBasicDetailsModal}
        getCompanyPageData={getCompanyPageData}
        companyPageData={companyPageData}
        companyApiLoading={companyApiLoading}
      />
      <AddIndustryFocusModal
        setShowIndustryFocusModal={setShowIndustryFocusModal}
        showIndustryFocusModal={showIndustryFocusModal}
        getAllIndustryFocus={getAllIndustryFocus}
        industryFocusData={industryFocusData}
      />
    </>
  )
}
